import React, { useCallback, useEffect, useState } from 'react';
import { ArrowPathIcon } from '@heroicons/react/24/solid';
import {
  UserGroupIcon,
  CheckBadgeIcon,
  ArrowTrendingUpIcon,
  SparklesIcon,
} from '@heroicons/react/24/outline';
import { getLeadFunnelReport } from '../../services/leadReportsApi';
import { LEAD_STAGES } from '../../utils/leadJourney';
import PageHeader from '../../components/ui/PageHeader';
import SectionCard from '../../components/ui/SectionCard';
import MetricTile from '../../components/ui/MetricTile';
import FilterChip from '../../components/ui/FilterChip';
import EmptyState from '../../components/ui/EmptyState';
import { ADMIN_PAGE_WIDTH, cx } from '../../components/ui/utils';

const PERIODS = [
  { days: 30, label: '30 días' },
  { days: 90, label: '90 días' },
  { days: 365, label: '1 año' },
];

const PRODUCT_LINE_LABELS = {
  erp: 'CRM/ERP a la medida',
  restaurante: 'Restaurantes',
  sin_clasificar: 'Sin clasificar',
};

const rateColor = (rate) => {
  if (rate >= 20) return 'text-emerald-600';
  if (rate >= 8) return 'text-amber-600';
  return 'text-slate-400';
};

const FunnelTable = ({ title, subtitle, rows, labelFor }) => (
  <SectionCard title={title} subtitle={subtitle} bodyClassName="p-0">
    <div className="overflow-x-auto">
      <table className="min-w-full text-sm">
        <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
          <tr>
            <th className="px-5 py-3 text-left">{labelFor}</th>
            <th className="px-4 py-3 text-right">Leads</th>
            {LEAD_STAGES.map((stage) => (
              <th key={stage.id} className="px-3 py-3 text-right" title={stage.description}>
                {stage.label}
              </th>
            ))}
            <th className="px-4 py-3 text-right">Score prom.</th>
            <th className="px-4 py-3 text-right" title="Clientes ÷ total de leads">
              Conversión
            </th>
            <th
              className="px-5 py-3 text-right"
              title="Clientes ÷ (clientes + inactivos). Excluye lo que sigue abierto."
            >
              Cierre
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {rows.length === 0 ? (
            <tr>
              <td colSpan={LEAD_STAGES.length + 5} className="px-5 py-6">
                <EmptyState label="Sin datos en este período" />
              </td>
            </tr>
          ) : (
            rows.map((row) => (
              <tr key={row.key} className="hover:bg-slate-50">
                <td className="px-5 py-3 font-medium text-slate-900">
                  {labelFor === 'Línea de producto'
                    ? PRODUCT_LINE_LABELS[row.key] || row.key
                    : row.key}
                </td>
                <td className="px-4 py-3 text-right font-semibold text-slate-700">{row.total}</td>
                {LEAD_STAGES.map((stage) => (
                  <td key={stage.id} className="px-3 py-3 text-right text-slate-500">
                    {row.stages?.[stage.id] || 0}
                  </td>
                ))}
                <td className="px-4 py-3 text-right text-slate-500">{row.avgScore ?? '—'}</td>
                <td className={cx('px-4 py-3 text-right font-semibold', rateColor(row.conversionRate))}>
                  {row.conversionRate}%
                </td>
                <td className={cx('px-5 py-3 text-right font-semibold', rateColor(row.closeRate))}>
                  {row.closeRate}%
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  </SectionCard>
);

const LeadFunnelReport = () => {
  const [report, setReport] = useState(null);
  const [days, setDays] = useState(90);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const loadReport = useCallback(async (period) => {
    setLoading(true);
    setError('');
    try {
      const data = await getLeadFunnelReport({ days: period });
      setReport(data);
    } catch (err) {
      console.error('Error cargando reporte de embudo:', err);
      setError(err.message || 'No se pudo cargar el reporte');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadReport(days);
  }, [days, loadReport]);

  const totals = report?.totals;

  return (
    <div className="min-h-screen bg-slate-50">
      <div className={cx('mx-auto space-y-6 px-4 py-6 sm:px-6 lg:px-8', ADMIN_PAGE_WIDTH)}>
        <PageHeader
          breadcrumb={[{ label: 'Leads', to: '/admin/leads' }, { label: 'Embudo' }]}
          title="Embudo de conversión"
          description="Qué canal trae leads que cierran, no solo cuántos leads trae."
          actions={
            <>
              {PERIODS.map((period) => (
                <FilterChip
                  key={period.days}
                  label={period.label}
                  active={days === period.days}
                  onClick={() => setDays(period.days)}
                />
              ))}
              <button
                onClick={() => loadReport(days)}
                className="inline-flex items-center gap-2 rounded-xl bg-orange-500 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-orange-600"
              >
                <ArrowPathIcon className="h-5 w-5" />
                Recargar
              </button>
            </>
          }
        />

        {error && (
          <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-red-700">
            {error}
          </div>
        )}

        {loading ? (
          <p className="py-10 text-center text-slate-500">Cargando reporte…</p>
        ) : (
          report && (
            <>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
                <MetricTile
                  title="Leads totales"
                  value={totals.total}
                  icon={UserGroupIcon}
                  tone="blue"
                />
                <MetricTile
                  title="Convertidos"
                  value={totals.won}
                  icon={CheckBadgeIcon}
                  tone="emerald"
                />
                <MetricTile
                  title="Conversión"
                  value={`${totals.conversionRate}%`}
                  helper={`Cierre sobre resueltos: ${totals.closeRate}%`}
                  icon={ArrowTrendingUpIcon}
                  tone="amber"
                />
                <MetricTile
                  title="Score promedio"
                  value={totals.avgScore ?? '—'}
                  helper="Solo leads con cuestionario contestado"
                  icon={SparklesIcon}
                  tone="violet"
                />
              </div>

              <SectionCard
                title="Distribución por etapa"
                subtitle="Dónde se está acumulando el embudo."
              >
                <div className="space-y-3">
                  {LEAD_STAGES.map((stage) => {
                    const count = totals.stages?.[stage.id] || 0;
                    const pct = totals.total ? (count / totals.total) * 100 : 0;
                    return (
                      <div key={stage.id} className="flex items-center gap-4">
                        <span className="w-36 shrink-0 text-sm font-medium text-slate-600">
                          {stage.label}
                        </span>
                        <div className="h-6 flex-1 overflow-hidden rounded-lg bg-slate-100">
                          <div
                            className={cx('h-full transition-all', stage.dot)}
                            style={{ width: `${pct}%` }}
                          />
                        </div>
                        <span className="w-20 shrink-0 text-right text-sm font-semibold text-slate-700">
                          {count}
                          <span className="ml-1 text-xs font-normal text-slate-400">
                            {Math.round(pct)}%
                          </span>
                        </span>
                      </div>
                    );
                  })}
                </div>
              </SectionCard>

              <FunnelTable
                title="Por fuente"
                subtitle="Compara el retorno real de cada canal de captación."
                rows={report.bySource}
                labelFor="Fuente"
              />

              <FunnelTable
                title="Por línea de producto"
                subtitle="Cuál de las dos líneas convierte mejor."
                rows={report.byProductLine}
                labelFor="Línea de producto"
              />
            </>
          )
        )}
      </div>
    </div>
  );
};

export default LeadFunnelReport;
